import React from 'react';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { getActiveNavigationItem, getPageNavigation, NavigationItem, NavigationSection, SECTION_LABELS } from './navigation';
import { getNavigationIcon } from './navigationIcons';

interface SidebarProps {
  items: NavigationItem[];
  pathname: string;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ items, pathname, collapsed, onCollapsedChange }) => {
  const pageItems = getPageNavigation(items);
  const activeItem = getActiveNavigationItem(pageItems, pathname);
  const sections = pageItems.reduce<Array<{ section: NavigationSection; items: NavigationItem[] }>>((groups, item) => {
    const group = groups.find((entry) => entry.section === item.section);
    if (group) group.items.push(item);
    else groups.push({ section: item.section, items: [item] });
    return groups;
  }, []);

  return (
    <aside
      aria-label="Primary navigation"
      className={`${collapsed ? 'w-20' : 'w-72'} hidden h-full min-h-0 shrink-0 flex-col overflow-hidden bg-[#071a33] text-white shadow-xl transition-[width] duration-200 motion-reduce:transition-none xl:flex`}
    >
      <div className={`flex h-20 shrink-0 items-center border-b border-white/10 ${collapsed ? 'justify-center px-2' : 'justify-between px-5'}`}>
        {!collapsed && (
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-400">Hednesford Town</p>
            <p className="mt-1 text-sm font-semibold text-white">Command Centre</p>
          </div>
        )}
        <button
          type="button"
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          onClick={() => onCollapsedChange(!collapsed)}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-slate-300 hover:bg-white/10 hover:text-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-gold-600 dark:focus-visible:ring-club-gold-500"
        >
          {collapsed
            ? <ChevronDoubleRightIcon className="h-5 w-5" aria-hidden="true" />
            : <ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />}
        </button>
      </div>
      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-5">
        {sections.map(({ section, items: sectionItems }) => {
          return (
            <section key={section} aria-labelledby={`sidebar-section-${section}`} className="mb-6 last:mb-0">
              <h2 id={`sidebar-section-${section}`} className={`${collapsed ? 'sr-only' : 'px-3'} mb-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400`}>
                {SECTION_LABELS[section]}
              </h2>
              <div className="space-y-1">
                {sectionItems.map((item) => {
                  const Icon = getNavigationIcon(item.id);
                  const active = item.id === activeItem?.id;
                  return (
                    <Link
                      key={item.id}
                      to={item.href}
                      aria-current={active ? 'page' : undefined}
                      title={collapsed ? item.label : undefined}
                      className={`flex min-h-11 items-center rounded-lg border-l-2 ${collapsed ? 'justify-center px-2' : 'gap-3 px-3'} ${
                        active
                          ? 'border-amber-400 bg-white/10 text-amber-300'
                          : 'border-transparent text-slate-200 hover:bg-white/5 hover:text-white'
                      } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-gold-600 dark:focus-visible:ring-club-gold-500`}
                    >
                      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                      <span className={collapsed ? 'sr-only' : ''}>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </section>
          );
        })}
      </nav>
      <div className={`shrink-0 border-t border-white/10 p-3 ${collapsed ? 'flex justify-center' : ''}`}>
        <ThemeToggle compact={collapsed} />
      </div>
    </aside>
  );
};

export default Sidebar;
